import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SingleUserCombobox } from "../ui/SingleUserCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { DatePicker } from "../ui/date-picker";
// DED-1339: MCM Mapping Card integration
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { useTenancy } from '@/contexts/TenancyContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ControlCombobox } from '../ui/ControlCombobox';
import { ScrollArea } from '@/components/ui/scroll-area';
// DED-1141: Supporting documents for incidents
import { IncidentLinkedDocumentsSection } from './IncidentLinkedDocumentsSection';

// DED-1141: Updated incident creation with new fields
// DED-1339: Added controls for MCM integration
export function IncidentCreateDialog({ open, onOpenChange, onCreate, auditOptions, defaultAuditId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (incident: { 
    summary: string; 
    owner: string; 
    status: "Open" | "Closed"; 
    auditId?: string;
    dateOfIncident?: Date | string;
    impact?: string;
    dateClosed?: Date | string;
    closeComment?: string;
    controls?: string[];
    supportingDocuments?: string[]; // DED-1141: Supporting documents for incident
  }) => void;
  auditOptions: { id: string; label: string }[];
  defaultAuditId?: string;
}) {
  // DED-1339: Get tenancy context for MCM integration
  const { activeTenancy } = useTenancy();
  const [summary, setSummary] = useState("");
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState<"Open" | "Closed">("Open");
  // DED-1141: Removed auditId state - Link to Audit removed from incidents
  // DED-1141: New fields
  const [dateOfIncident, setDateOfIncident] = useState<Date | undefined>(undefined);
  const [impact, setImpact] = useState("");
  const [dateClosed, setDateClosed] = useState<Date | undefined>(undefined);
  const [closeComment, setCloseComment] = useState("");
  // DED-1339: Controls state for MCM integration
  const [controls, setControls] = useState<string[]>([]);
  // DED-1141: Supporting documents state
  const [supportingDocuments, setSupportingDocuments] = useState<string[]>([]);

  const resetForm = () => {
    setSummary("");
    setOwner("");
    setStatus("Open");
    setDateOfIncident(undefined);
    setImpact("");
    setDateClosed(undefined);
    setCloseComment("");
    // DED-1339: Reset controls
    setControls([]);
    // DED-1141: Reset supporting documents
    setSupportingDocuments([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Create Incident</DialogTitle>
            <DialogDescription>Fill in the details for the new incident.</DialogDescription>
          </div>
        <div className="flex flex-col gap-3 max-w-full overflow-hidden px-4 pb-4">
          <label className="flex flex-col gap-1">
            <span>Summary *</span>
            <Input value={summary} onChange={e => setSummary(e.target.value)} placeholder="Brief description of the incident" />
          </label>
          <label className="flex flex-col gap-1">
            <span>Owner *</span>
            <SingleUserCombobox
              selected={owner}
              setSelected={(value) => setOwner(value)}
            />
          </label>
          {/* DED-1141: Date of Incident field */}
          <label className="flex flex-col gap-1">
            <span>Date of Incident</span>
            <DatePicker
              date={dateOfIncident}
              setDate={setDateOfIncident}
              placeholder="Select date of incident"
            />
          </label>
          {/* DED-1141: Impact field */}
          <label className="flex flex-col gap-1">
            <span>Impact</span>
            <Textarea 
              value={impact} 
              onChange={e => setImpact(e.target.value)} 
              placeholder="Describe the impact of this incident"
              className="min-h-[80px]"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span>Status</span>
            <Select value={status} onValueChange={value => setStatus(value as any)}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </label>
          {/* DED-1141: Show close fields when status is Closed */}
          {status === "Closed" && (
            <>
              <label className="flex flex-col gap-1">
                <span>Date Closed</span>
                <DatePicker
                  date={dateClosed}
                  setDate={setDateClosed}
                  placeholder="Select close date"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span>Close Comment</span>
                <Textarea 
                  value={closeComment} 
                  onChange={e => setCloseComment(e.target.value)} 
                  placeholder="Add a comment about the closure"
                  className="min-h-[80px]"
                />
              </label>
            </>
          )}
          {/* DED-1141: Removed 'Link to Audit' field */}

          {/* DED-1141: Supporting Document Section */}
          <div className="py-2">
            <IncidentLinkedDocumentsSection
              linkedDocuments={supportingDocuments}
              onDocumentsChange={setSupportingDocuments}
              inheritedControls={controls}
              incidentTitle={summary}
            />
          </div>

          {/* DED-1339: MCM Integration - Controls Section */}
          <Accordion type="single" collapsible defaultValue="controls">
            <AccordionItem value="controls" className="border rounded-lg shadow-sm mb-2 bg-white dark:bg-background">
              <AccordionTrigger className="px-4 py-3 text-base font-medium flex items-center gap-2 mb-4">
                <span className="flex-1 text-left">Controls / Clauses</span>
                <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {controls.length > 0 ? `${controls.length} selected` : 'None selected'}
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <ScrollArea className="max-h-[520px] overflow-auto">
                  <ControlCombobox
                    selected={controls}
                    setSelected={(value) => setControls((value as Array<string | number>).map(String))}
                    className="border border-gray-200 rounded-md focus:border-primary focus:ring-1 focus:ring-primary w-full"
                  />
                </ScrollArea>
              </AccordionContent>
            </AccordionItem>
            <MCMMappingsCard
              tenancyId={activeTenancy?.id}
              isReadOnly={false}
              controlIds={controls.map(String)}
              onEnsureControlLinked={(controlId) => setControls((prev) => Array.from(new Set([...prev, String(controlId)])))}
              collapsible
            />
          </Accordion>
        </div>
        <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => {
              onCreate({ 
                summary, 
                owner, 
                status,
                dateOfIncident: dateOfIncident?.toISOString(),
                impact: impact || undefined,
                dateClosed: status === "Closed" ? dateClosed?.toISOString() : undefined,
                closeComment: status === "Closed" ? closeComment || undefined : undefined,
                // DED-1339: Include controls for MCM
                controls,
                // DED-1141: Include supporting documents
                supportingDocuments,
              });
              onOpenChange(false);
              resetForm();
            }}
            disabled={!summary || !owner}
          >
            Create
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
